import React, { useEffect, useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Section, Button, Select, Badge, Empty, Input } from "@/components/ui/kit";
import { fmtCurrency, fmtDate } from "@/utils/format";
import { Plus, Search, ChevronDown } from "lucide-react";

export default function DonationsList() {
  const [rows, setRows] = useState([]);
  const [purposes, setPurposes] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);

  // Filter & Pagination States
  const [sectorId, setSectorId] = useState("");
  const [projectId, setProjectId] = useState("");
  const [activityId, setActivityId] = useState("");
  const [type, setType] = useState("");
  const [payment, setPayment] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  // Fetch Purposes & Payment Modes
  useEffect(() => {
    api.get("/donation-purposes").then(r => setPurposes(r.data.data));
    api.get("/master/payment-modes").then(r => setPaymentModes(r.data.data || r.data));
  }, []);

  // Compute Cascading Lists
  const sectors = useMemo(() => purposes.filter(p => p.type === "SECTOR"), [purposes]);
  const projects = useMemo(() => purposes.filter(p => p.type === "PROJECT" && (!sectorId || String(p.parentId) === String(sectorId))), [purposes, sectorId]);
  const activities = useMemo(() => purposes.filter(p => p.type === "ACTIVITY" && (!projectId || String(p.parentId) === String(projectId))), [purposes, projectId]);

  // Determine the most specific purpose ID to send to the backend
  const activePurposeId = activityId || projectId || sectorId || "";

  // Fetch Donations from the API
  useEffect(() => {
    setLoading(true);
    api.get("/donations", {
      params: {
        page,
        size: 20,
        donationType: type || undefined,
        paymentModeId: payment || undefined,
        purposeId: activePurposeId || undefined
      }
    })
      .then(r => {
        setRows(r.data.data.content);
        setTotalPages(r.data.data.totalPages || 1);
      })
      .finally(() => setLoading(false));
  }, [page, type, payment, activePurposeId]);

  // Local Search Filter (snappy filtering for visible rows)
  const displayedRows = useMemo(() => {
    if (!searchTerm) return rows;
    const term = searchTerm.toLowerCase();
    return rows.filter(r =>
      String(r.donationId).includes(term) ||
      r.donorName?.toLowerCase().includes(term) ||
      r.purposeName?.toLowerCase().includes(term) ||
      r.receiptNumber?.toLowerCase().includes(term)
    );
  }, [rows, searchTerm]);

  return (
    <div className="space-y-6">
      <Section
        title="Donations"
        kicker="Contributions"
        right={<Link to="/admin/donations/new"><Button data-testid="add-donation-btn"><Plus className="w-4 h-4 mr-2" /> Add donation</Button></Link>}
      />

      <div className="rounded-xl border border-emerald-900/10 bg-white p-4">

        {/* Filters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="relative">
            <Input
              placeholder="Search donor, ID, receipt..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-9"
            />
            <Search className="w-4 h-4 text-emerald-900/40 absolute left-3 top-3 pointer-events-none" />
          </div>

          <SearchableSelect
            placeholder="Search Sector..."
            options={sectors}
            value={sectorId}
            onChange={val => { setSectorId(val); setProjectId(""); setActivityId(""); setPage(0); }}
          />

          <SearchableSelect
            placeholder="Search Project..."
            options={projects}
            value={projectId}
            disabled={!sectorId && projects.length === 0}
            onChange={val => { setProjectId(val); setActivityId(""); setPage(0); }}
          />

          <SearchableSelect
            placeholder="Search Activity..."
            options={activities}
            value={activityId}
            disabled={!projectId && activities.length === 0}
            onChange={val => { setActivityId(val); setPage(0); }}
          />

          {/* Standard Selects for Type & Payment Mode */}
          <Select testid="donation-type-filter" value={type} onChange={e => { setType(e.target.value); setPage(0); }}>
            <option value="">All donation types</option>
            <option value="SELF">Self</option>
            <option value="MATCHED">Matched</option>
          </Select>


          <Select testid="donation-payment-filter" value={payment} onChange={e => { setPayment(e.target.value); setPage(0); }}>
            <option value="">All payment modes</option>
            {paymentModes.map(p => (
              <option key={p.payment_mode_id || p.paymentModeId || p.id} value={p.payment_mode_id || p.paymentModeId || p.id}>
                {p.description}
              </option>
            ))}
          </Select>
        </div>

        {/* Data Table */}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-emerald-800/70 border-b border-emerald-900/10">
                <th className="py-2 pr-4">ID</th>
                <th className="py-2 pr-4">Donor</th>
                <th className="py-2 pr-4">Date</th>
                <th className="py-2 pr-4">Amount & Currency</th>
                <th className="py-2 pr-4">Purpose</th>
                <th className="py-2 pr-4">Payment & Ref</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Receipt</th>
                <th className="py-2 pr-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {displayedRows.map(r => {
                const currencyCode = r.amountType || r.currencyCode || "INR";
                return (
                  <tr key={r.donationId} data-testid={`donation-row-${r.donationId}`} className="border-b border-emerald-900/5 hover:bg-emerald-50/40">
                    <td className="py-3 pr-4 font-mono text-emerald-900">#{r.donationId}</td>

                    <td className="py-3 pr-4 font-semibold text-emerald-950">
                      <Link to={`/admin/donors/${r.donorId}`} className="hover:underline text-emerald-800 transition-colors">
                        {r.salutation ? `${r.salutation} ` : ""}{r.donorName}
                      </Link>
                    </td>

                    <td className="py-3 pr-4 text-muted-foreground">{fmtDate(r.donationDate)}</td>

                    <td className="py-3 pr-4 font-mono font-semibold text-emerald-900">
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-950/10 text-emerald-800 font-sans font-medium">
                          {currencyCode}
                        </span>
                        <span>{fmtCurrency(r.amount, currencyCode)}</span>
                      </div>
                    </td>

                    <td className="py-3 pr-4">{r.purposeName || "—"} <span className="text-[10px] text-muted-foreground uppercase">{r.purposeType}</span></td>

                    <td className="py-3 pr-4">
                      <div className="font-mono text-xs">{r.paymentMode || r.paymentModeDescription}</div>
                      {r.transactionReferenceNumber && (
                        <div className="text-[10px] text-muted-foreground truncate max-w-[120px]" title={r.transactionReferenceNumber}>
                          {r.transactionReferenceNumber}
                        </div>
                      )}
                    </td>

                    <td className="py-3 pr-4">
                      {r.donationType === "MATCHED" ? <Badge tone="warm">Matched</Badge> : <Badge tone="brand">Self</Badge>}
                    </td>
                    <td className="py-3 pr-4 font-mono text-xs text-muted-foreground">{r.receiptNumber || "—"}</td>

                    <td className="py-3 pr-4 text-right">
                      <Link to={`/admin/donations/${r.donationId}`} data-testid={`view-donation-${r.donationId}`} className="text-emerald-800 font-semibold text-xs hover:underline">
                        View
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {!loading && !displayedRows.length && (
                <tr><td colSpan={9}><Empty title="No donations found" note="Try adjusting your filters or search term." testid="donations-empty" /></td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div className="mt-4 flex items-center justify-between text-xs">
          <div className="text-muted-foreground">Page {page + 1} of {Math.max(1, totalPages)}</div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" data-testid="donations-prev" disabled={page === 0 || loading} onClick={() => setPage(p => p - 1)}>Prev</Button>
            <Button variant="secondary" size="sm" data-testid="donations-next" disabled={page + 1 >= totalPages || loading} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- Custom Searchable Select Component ---
function SearchableSelect({ value, onChange, options, placeholder, disabled }) {
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Sync internal search state with the selected value prop
  useEffect(() => {
    if (value) {
      const selectedOpt = options.find(o => String(o.id) === String(value));
      if (selectedOpt) setSearch(selectedOpt.name);
    } else {
      setSearch("");
    }
  }, [value, options]);

  // Handle clicking outside the dropdown to close it
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        const selectedOpt = options.find(o => String(o.id) === String(value));
        setSearch(selectedOpt ? selectedOpt.name : "");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [value, options]);

  const filtered = options.filter(o => o.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="relative" ref={wrapperRef}>
      <Input
        placeholder={placeholder}
        value={search}
        disabled={disabled}
        onChange={e => {
          setSearch(e.target.value);
          setIsOpen(true);
          if (value) onChange(""); // clear actual selection if user starts typing a new search
        }}
        onClick={() => { if (!disabled) setIsOpen(true); }}
      />
      <ChevronDown className={`absolute right-3 top-3 w-4 h-4 pointer-events-none transition-transform ${isOpen ? "rotate-180" : ""} ${disabled ? "text-emerald-900/20" : "text-emerald-900/40"}`} />

      {isOpen && !disabled && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-y-auto bg-white border border-emerald-900/10 rounded-lg shadow-xl">
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-center text-muted-foreground">No matches found</div>
          ) : (
            <>
              {value && (
                <button
                  type="button"
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-b border-emerald-50 transition-colors"
                  onClick={() => { onChange(""); setSearch(""); setIsOpen(false); }}
                >
                  Clear Selection
                </button>
              )}
              {filtered.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-emerald-50 transition-colors ${String(value) === String(opt.id) ? "bg-emerald-50/50 font-semibold text-emerald-900" : "text-emerald-950"}`}
                  onClick={() => {
                    onChange(opt.id);
                    setSearch(opt.name);
                    setIsOpen(false);
                  }}
                >
                  {opt.name}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}